import { Badge, Text } from "@merge-api/merge-javascript-shared";
import { Columns } from "lucide-react";
import BlueprintCard from "./BlueprintCard";
import { BlueprintPriority, SyncMode } from "./types";

const PhaseBlock = ({
  priority,
  entries,
  maxConcurrentBlueprints,
  integrationID,
  syncMode,
  isLast,
}: {
  priority: number;
  entries: BlueprintPriority[];
  maxConcurrentBlueprints: number | null;
  integrationID: string;
  syncMode: SyncMode;
  isLast: boolean;
}) => {
  const isConcurrent = entries.length > 1;

  const resolvedEntries = entries.map((entry) => {
    const useInitialOverride =
      syncMode === "initial" && entry.should_run_different_initial_sync_blueprint !== null;

    if (useInitialOverride) {
      return {
        bp: {
          id: entry.should_run_different_initial_sync_blueprint!,
          name: entry.initial_sync_blueprint_name ?? "",
          humanName: entry.initial_sync_blueprint_human_name,
          publishedVersionId: entry.initial_sync_blueprint_latest_published_version_id,
        },
        isInitialOverride: true,
        key: entry.blueprint,
      };
    }
    return {
      bp: {
        id: entry.blueprint,
        name: entry.blueprint_name,
        humanName: entry.blueprint_human_name,
        publishedVersionId: entry.blueprint_latest_published_version_id,
      },
      isInitialOverride: false,
      key: entry.blueprint,
    };
  });

  return (
    <>
      {/* Grid cell 1: pill + connector */}
      <div className="flex flex-col items-center">
        <div className="rounded bg-blue-0 px-3 py-1.5 text-center min-w-[80px]">
          <span className="text-[10px] font-semibold text-blue-70 uppercase tracking-wider block">
            Priority
          </span>
          <Text variant="h4" className="text-blue-70 leading-none mt-0.5">
            {priority}
          </Text>
        </div>
        {!isLast && (
          <div className="flex flex-col items-center flex-1 pt-2">
            <div className="w-px flex-1 bg-gray-200" />
          </div>
        )}
      </div>

      {/* Grid cell 2: blueprints */}
      <div className="min-w-0 pb-6">
        {isConcurrent && (
          <div className="flex items-center gap-1.5 mb-2">
            <Columns size={13} className="text-gray-400" />
            <Text variant="sm" className="text-gray-500 font-medium">
              runs concurrently
            </Text>
            {maxConcurrentBlueprints != null && entries.length > maxConcurrentBlueprints && (
              <Badge color="yellow">batches of {maxConcurrentBlueprints}</Badge>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {resolvedEntries.map(({ bp, isInitialOverride, key }) => (
            <BlueprintCard
              key={key}
              bp={bp}
              integrationID={integrationID}
              isInitialOverride={isInitialOverride}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default PhaseBlock;
